import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Music, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/kit";
import { API, api, errMsg } from "@/lib/api";
import { LogoEnhance } from "@/components/LogoEnhance";

export function LogosRow({ className = "", light }) {
  const [keys, setKeys] = useState([]);
  useEffect(() => {
    api.get("/public/logos").then((r) => setKeys(r.data)).catch(() => {});
  }, []);
  if (!keys.length) return null;
  return (
    <div className={`flex flex-wrap items-center gap-5 ${light ? "rounded-2xl bg-white/90 px-4 py-2 w-fit" : ""} ${className}`} data-testid="logos-row">
      {keys.map((k) => <img key={k} src={`${API}/public/logo/${k}`} alt={k} className="h-10 sm:h-12 w-auto object-contain" />)}
    </div>
  );
}

function UploadSlot({ label, has, accept, url, onUpload, onDelete, testId, preview, extra }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    await onUpload(f);
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <div className="rounded-xl border border-[#D4E6EC] bg-white p-4 space-y-3" data-testid={testId}>
      <div className="text-sm font-medium">{label}</div>
      {has && preview}
      {!has && <div className="text-xs text-[#7B929C]">Nessun file caricato</div>}
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid={`${testId}-input`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testId}-upload`}>{busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />} {has ? "Sostituisci" : "Carica"}</Button>
        {has && <Button size="sm" variant="ghost" className="rounded-full text-[#991B1B]" onClick={onDelete} data-testid={`${testId}-delete`}><Trash2 size={13} /></Button>}
        {has && extra}
      </div>
    </div>
  );
}

export function BrandSettings() {
  const [b, setB] = useState(null);
  const [v, setV] = useState(0);
  const load = () => api.get("/brand").then((r) => { setB(r.data); setV(Date.now()); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  const up = (url) => async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(url, fd);
      toast.success("Caricato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = (url) => async () => {
    if (!window.confirm("Eliminare questo file?")) return;
    await api.delete(url);
    load();
  };
  if (!b) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="space-y-6" data-testid="brand-settings">
      <Panel title="Loghi delle strutture">
        <p className="text-sm text-[#47606B]">I loghi compaiono su preventivi, contratti, ricevute, schede, brochure, preventivo online e area cliente. Potete caricare PNG, JPG o PDF (max 10 MB): dal PDF viene presa la prima pagina, ritagliata sul logo. Meglio con sfondo trasparente. Se il logo è sgranato, piccolo o sporco, usate «Migliora con IA»: vedrete prima e dopo e deciderete voi se usarlo.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {b.logos.map((l) => <UploadSlot key={l.key} label={l.label} has={l.has} accept="image/png,image/jpeg,image/webp,application/pdf,.pdf" testId={`logo-${l.key}`} onUpload={up(`/brand/logo/${l.key}`)} onDelete={del(`/brand/logo/${l.key}`)} extra={<LogoEnhance k={l.key} label={l.label} onDone={load} />}
            preview={<div className="h-20 rounded-lg bg-[#F5FAFB] flex items-center justify-center"><img src={`${API}/public/logo/${l.key}?v=${v}`} alt={l.label} className="max-h-16 max-w-full object-contain" /></div>} />)}
        </div>
      </Panel>
      <Panel title="Musica della brochure">
        <p className="text-sm text-[#47606B]">Una traccia per tipo di evento (MP3, M4A, WAV o OGG, max 20 MB), di cui avete i diritti. Parte quando il cliente inizia a sfogliare; «Altro» vale per tutti gli eventi senza traccia propria.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {b.music.map((m) => <UploadSlot key={m.evento} label={m.evento} has={m.has} accept="audio/*,.mp3,.m4a,.wav,.ogg" testId={`music-${m.evento}`} onUpload={up(`/brand/music/${m.evento}`)} onDelete={del(`/brand/music/${m.evento}`)}
            preview={<div className="space-y-2"><div className="text-xs text-[#47606B] flex items-center gap-1 min-w-0"><Music size={13} className="shrink-0" /><span className="truncate">{m.name}</span></div><audio controls src={`${API}/public/music/${m.evento}?v=${v}`} className="w-full h-9" /></div>} />)}
        </div>
      </Panel>
    </div>
  );
}
